"use server";

import { redirect } from "next/navigation";
import { z } from "zod";
import { clientIp } from "@/lib/auth";
import { confirmOptin, newsletterEnabled, subscribe, unsubscribe, UUID_RE } from "@/lib/newsletter";

/* Öffentliche Actions (ohne Login). Schutz: Zod, Honeypot-Feld und Begrenzung pro IP. */

export type SignupState = { kind: "idle" | "ok" | "error"; text: string; email?: string };

const SOURCES = ["startseite", "newsletter", "beitrag", "seite", "reisetipps"] as const;
const Signup = z.object({
  email: z.string().trim().toLowerCase().max(254).pipe(z.email()),
  source: z.enum(SOURCES).catch("newsletter"),
  website: z.string().max(0).optional(),
});

const WINDOW_MS = 15 * 60_000;
const MAX_PER_WINDOW = 5;
const g = globalThis as typeof globalThis & { gcgNlRate?: Map<string, number[]> };
function limited(ip: string) {
  const m = (g.gcgNlRate ??= new Map<string, number[]>());
  const now = Date.now();
  const hits = (m.get(ip) ?? []).filter((t) => now - t < WINDOW_MS);
  hits.push(now);
  m.set(ip, hits);
  if (m.size > 5000) for (const [k, v] of m) if (now - v[v.length - 1] > WINDOW_MS) m.delete(k);
  return hits.length > MAX_PER_WINDOW;
}

export async function signup(_prev: SignupState, formData: FormData): Promise<SignupState> {
  const parsed = Signup.safeParse({ email: formData.get("email"), source: formData.get("source"), website: formData.get("website") ?? "" });
  if (!parsed.success) {
    // Ausgefülltes Honeypot-Feld: wie Erfolg antworten, damit Bots nichts lernen.
    if (parsed.error.issues.some((i) => i.path[0] === "website")) return { kind: "ok", text: "" };
    return { kind: "error", text: "Bitte gib eine gültige E-Mail-Adresse ein." };
  }
  if (!newsletterEnabled()) return { kind: "error", text: "Der Newsletter startet in Kürze. Schau bald wieder vorbei." };
  if (limited(await clientIp())) return { kind: "error", text: "Zu viele Anmeldungen in kurzer Zeit. Bitte versuch es in einer Viertelstunde noch einmal." };
  try {
    const res = await subscribe(parsed.data.email, parsed.data.source);
    return {
      kind: "ok", email: parsed.data.email,
      text: res === "queued"
        ? "Danke! Die Bestätigungsmail kann diesmal ein paar Minuten dauern. Bitte klick dann auf den Link darin."
        : "Fast geschafft! Wir haben dir eine Mail geschickt. Bitte bestätige die Anmeldung über den Link darin.",
    };
  } catch (e) {
    console.error("Newsletter-Anmeldung:", e instanceof Error ? e.message : e);
    return { kind: "error", text: "Die Anmeldung hat gerade nicht geklappt. Bitte versuch es später noch einmal." };
  }
}

const Uuid = z.string().regex(UUID_RE);

export async function confirmSignup(formData: FormData) {
  const sub = Uuid.safeParse(formData.get("sub"));
  if (!sub.success) redirect("/newsletter");
  let res: "ok" | "invalid" | "error";
  try {
    res = await confirmOptin(sub.data, await clientIp());
  } catch (e) {
    console.error("Newsletter-Bestätigung:", e instanceof Error ? e.message : e);
    res = "error";
  }
  if (res === "ok") redirect("/newsletter/bestaetigt");
  redirect(`/nl/subscription/optin/${sub.data}?fehler=${res === "invalid" ? "link" : "server"}`);
}

export async function unsubscribeFromMail(formData: FormData) {
  const camp = Uuid.safeParse(formData.get("camp"));
  const sub = Uuid.safeParse(formData.get("sub"));
  if (!camp.success || !sub.success) redirect("/newsletter");
  const wipe = formData.get("wipe") === "1";
  let res: "ok" | "invalid" | "error";
  try {
    res = await unsubscribe(camp.data, sub.data, wipe);
  } catch (e) {
    console.error("Newsletter-Abmeldung:", e instanceof Error ? e.message : e);
    res = "error";
  }
  if (res === "ok") redirect(`/newsletter/abgemeldet${wipe ? "?geloescht=1" : ""}`);
  redirect(`/nl/subscription/${camp.data}/${sub.data}?fehler=${res === "invalid" ? "link" : "server"}`);
}
